// src/components/avisEnseignants/presentationAvis.jsx
// ============================================================================
// L'écriture d'un avis d'enseignant, partagée par la vie scolaire (onglet
// « Avis des enseignants ») et le suivi des enseignants (onglet « Avis » de la
// page d'un enseignant)
// ============================================================================
//
// Une seule façon de dire un avis à l'écran : sinon la vie scolaire lirait
// « Lu » là où la direction lit « Traité » pour le même avis. Rien ici ne
// décide : le statut, la tonalité et les libellés viennent du serveur, qui
// tient la liste fermée des libellés (AvisEnseignantService).
// ============================================================================

import React from 'react';
import { STATUT_OPTIONS } from '../../hooks/useVieScolaire';
import { dateCourte, dateHeure, estVrai, libelleDe, nomEleve, nomPersonne } from '../suiviEnseignants/formatSuivi';

// Vert pour ce qui va bien, ambre pour ce qui est à surveiller : un avis de
// vigilance n'est pas un refus ni une faute (le rouge du modèle visuel), c'est
// un signal à regarder.
const TONS_LIBELLE = {
  positif: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  vigilance: 'border-amber-200 bg-amber-50 text-amber-800'
};
const TON_NEUTRE = 'border-gray-200 bg-gray-100 text-gray-600';

export const BadgeLibelle = ({ libelle }) => (
  <span className={`inline-flex h-6 items-center whitespace-nowrap rounded-lg border px-2.5 text-xs font-medium ${TONS_LIBELLE[libelle?.tonalite] || TON_NEUTRE}`}>
    {libelle?.libelle || libelle?.code}
  </span>
);

/** Les libellés d'un avis, ceux qui vont bien d'abord, dans l'ordre du serveur. */
export const LibellesAvis = ({ libelles }) => {
  const liste = Array.isArray(libelles) ? libelles : [];
  const tries = [
    ...liste.filter((l) => l?.tonalite === 'positif'),
    ...liste.filter((l) => l?.tonalite !== 'positif')
  ];
  return (
    <div className="flex flex-wrap gap-1.5">
      {tries.map((l) => <BadgeLibelle key={l.code || l.libelle} libelle={l} />)}
    </div>
  );
};

// Le statut de l'AVIS (pas celui de l'incident qu'il a pu devenir). Badges de
// largeur fixe : ils se répètent d'une ligne à l'autre.
export const STATUTS_AVIS = {
  nouveau: ['Nouveau', 'border-[#246990]/20 bg-[#eef7fb] text-[#246990]'],
  lu: ['Lu', 'border-gray-200 bg-gray-100 text-gray-600'],
  // La teinte des incidents de la page Vie scolaire, pour qu'on les reconnaisse.
  incident: ['Devenu incident', 'border-orange-200 bg-orange-50 text-orange-700']
};

const LIBELLES_STATUT_EVENEMENT = Object.fromEntries(STATUT_OPTIONS.map((o) => [o.value, o.label]));

export const BadgeStatutAvis = ({ statut }) => {
  const [libelle, classes] = STATUTS_AVIS[statut] || [statut || '-', TON_NEUTRE];
  return (
    <span className={`inline-flex h-6 w-[132px] items-center justify-center whitespace-nowrap rounded-lg border text-xs font-medium ${classes}`}>
      {libelle}
    </span>
  );
};

/** « Lu par Mme X, le 17 nov. 2026 à 08:10 », « Incident : En cours » ; rien pour un avis nouveau. */
export function detailStatut(avis) {
  if (avis?.statut === 'lu') {
    const quand = dateHeure(avis.lu_le);
    if (avis.lu_par_nom) return `Lu par ${avis.lu_par_nom}${quand ? `, le ${quand}` : ''}`;
    return quand ? `Lu le ${quand}` : '';
  }
  if (avis?.statut === 'incident') {
    const statut = avis.incident?.statut;
    // L'incident a été supprimé depuis par la vie scolaire : le serveur le
    // signale par `supprime` (événement effacé ou introuvable) — il rend alors
    // encore son dernier statut, qu'il ne faut pas afficher comme s'il vivait
    // (« Incident : Nouveau » pour un incident qui n'existe plus). Sans objet
    // `incident` du tout, on ne devine pas davantage.
    if (!avis.incident || estVrai(avis.incident.supprime)) return "L'incident a depuis été supprimé de la vie scolaire.";
    return `Incident : ${LIBELLES_STATUT_EVENEMENT[statut] || statut || 'enregistré'}`;
  }
  return '';
}

/** « Arnaud MOUKOKO · Histoire-Géographie » ; « · Titulaire » n'est pas deviné : sans matière, le nom seul. */
export function auteurAvis(avis) {
  const nom = nomPersonne(avis?.enseignant);
  const matiere = libelleDe(avis?.matiere);
  return [nom, matiere].filter(Boolean).join(' · ');
}

/** L'intitulé d'un avis pour un lecteur d'écran : « l'avis du 17 nov. sur MBEMBA Rodrigue ». */
export const intituleAvis = (avis) => `l'avis du ${dateCourte(avis?.date_avis)} sur ${nomEleve(avis?.eleve)}`;

/**
 * Une ligne d'avis. `actions` reçoit les boutons de la vie scolaire (rien en
 * lecture seule) ; `masquerAuteur` sert sur la page d'un enseignant, où
 * l'auteur est toujours le même — sa matière, elle, reste écrite : un
 * enseignant de deux matières donne ses avis dans l'une ou l'autre.
 */
export const LigneAvis = ({ avis, actions = null, masquerAuteur = false }) => {
  const classe = libelleDe(avis?.eleve?.classe);
  const detail = detailStatut(avis);
  const auteur = masquerAuteur ? libelleDe(avis?.matiere) : auteurAvis(avis);
  return (
    <li className="flex flex-col gap-2 py-3.5 lg:flex-row lg:items-start lg:gap-4">
      <p className="shrink-0 text-xs font-semibold text-gray-600 lg:w-[70px] lg:pt-0.5">{dateCourte(avis?.date_avis)}</p>
      <div className="shrink-0 lg:w-[200px]">
        <p className="text-sm font-semibold text-gray-900">{nomEleve(avis?.eleve) || 'Élève'}</p>
        {classe && <p className="text-xs text-gray-500">{classe}</p>}
      </div>
      <div className="min-w-0 flex-1">
        <LibellesAvis libelles={avis?.libelles} />
        {avis?.commentaire && (
          <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-gray-700">{avis.commentaire}</p>
        )}
        {auteur && <p className="mt-1 text-xs text-gray-500">{auteur}</p>}
      </div>
      <div className="shrink-0 lg:w-[310px]">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <BadgeStatutAvis statut={avis?.statut} />
          {detail && <p className="text-[11px] leading-snug text-gray-500">{detail}</p>}
        </div>
        {actions && <div className="mt-2 flex flex-wrap gap-2">{actions}</div>}
      </div>
    </li>
  );
};
